import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { MouseEventHandler, ReactNode } from "react";
import { Link } from "../Link";
import { NavigationTag } from "../NavigationTag";
import { EcologiTheme } from "../theme";
import { NavigationTagType } from "./navigation";

const LinkContainer = styled.div<{ isActive: boolean }>`
  position: relative;
  display: flex;
  font-size: 12px;
  align-items: center;

  background-color: ${({ theme, isActive }) =>
    isActive
      ? theme.colors.opacity(theme.colors.snowdrop, 0.1)
      : "transparent"};

  :hover {
    background-color: ${({ theme }) =>
      theme.colors.opacity(theme.colors.snowdrop, 0.05)};
  }
`;

const StyledLink = styled(Link)`
  flex: 1;
  background-color: transparent;

  :hover {
    background-color: transparent !important;
  }

  p {
    font-weight: 400 !important;
  }
`;

type SharedMenuOptionProps = {
  isActive: boolean;
  theme: EcologiTheme;
};

export const sharedMenuOptionStyles = ({
  isActive,
  theme,
}: SharedMenuOptionProps) => css`
  cursor: pointer;
  display: flex;
  align-items: center;
  border: none;
  outline: none;
  padding: 8px 0;
  position: relative;
  text-decoration: none;
  background-color: transparent;

  p {
    color: ${theme.colors.mistDark};
    font-size: 14px;
    padding: 0 8px 0 24px;
    text-align: left;
    text-decoration: none;
  }

  &:hover,
  &:focus {
    background-color: ${theme.colors.opacity(theme.colors.snowdrop, 0.03)};

    p {
      color: ${theme.colors.snowdrop};
    }
  }

  ${!isActive &&
  css`
    &:focus::before {
      background-color: ${theme.colors.snowdrop};
      content: "";
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 8px;
    }
  `}

  ${isActive &&
  css`
    position: relative;

    > p,
    > a p {
      color: ${theme.colors.snowdrop};
      font-weight: bold;
    }

    &::before {
      background-color: ${theme.colors.jungle};
      content: "";
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 8px;
    }
  `}
`;

type NavLinkProps = {
  rightSlot?: ReactNode;
  isActive: boolean;
  onClick: MouseEventHandler<HTMLAnchorElement>;
  href: string;
  isOpenInNewTab?: boolean;
  tag?: NavigationTagType;
  children: ReactNode;
};

export const NavLink = styled(
  ({
    href,
    onClick,
    rightSlot,
    isOpenInNewTab,
    tag,
    ...props
  }: NavLinkProps) => {
    const { isActive, ...narrowedProps } = props;

    return href.startsWith("/api/") ? (
      <a href={href} onClick={onClick} {...narrowedProps}>
        {props["children"]}
      </a>
    ) : (
      <LinkContainer isActive={isActive}>
        <StyledLink
          href={href}
          onClick={onClick}
          target={isOpenInNewTab ? "_blank" : undefined}
          {...narrowedProps}
        >
          {props.children}
          {tag && (
            <NavigationTag themeColor={tag?.themeColor}>
              {tag.text}
            </NavigationTag>
          )}
        </StyledLink>
        {rightSlot}
      </LinkContainer>
    );
  }
)`
  ${sharedMenuOptionStyles}
`;

export const NavItemDivider = styled.div`
  border-top: 2px solid #000;
  margin: 16px 24px;
`;
